import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Alert,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox
} from '@mui/material';
import {
    Policy as PolicyIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { EIP_APPLICATION_SITE, EIP_APPLICATION_URL } from '../../config/eipConfig';

const POLICY_KEYS = [
    'registrationFee',
    'financialAid',
    'refunds',
    'cancellation',
    'certificates',
    'responsibilities',
    'children',
    'nonDiscrimination'
];

const EnrollmentFormModal = ({ open, onClose, onEnroll, sessionId, loading = false, enrollmentError = '', enrollmentSuccess = '' }) => {
    const { t } = useTranslation('enrollment');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [error, setError] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);

    useEffect(() => {
        if (!open) {
            setPaymentMethod('');
            setError('');
            setTermsAccepted(false);
        }
    }, [open]);

    const handleSubmit = () => {
        if (!paymentMethod) {
            setError(t('selectPayment'));
            return;
        }

        if (!termsAccepted) {
            setError(t('acceptTermsRequired'));
            return;
        }

        setError('');
        onEnroll(sessionId, paymentMethod);
    };

    const handleClose = () => {
        setPaymentMethod('');
        setError('');
        setTermsAccepted(false);
        onClose();
    };

    const renderPolicySection = (key) => {
        if (key === 'financialAid') {
            return (
                <Box key={key}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
                        {t('policies.financialAid.title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6, fontSize: '0.875rem', mb: 1 }}>
                        {t('policies.financialAid.content1')}{' '}
                        <a href={EIP_APPLICATION_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {EIP_APPLICATION_SITE}
                        </a>.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6, fontSize: '0.875rem' }}>
                        {t('policies.financialAid.content2')}{' '}
                        <a href="mailto:eip@albany.edu" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            eip@albany.edu
                        </a>
                        {t('policies.financialAid.content3')}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box key={key}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
                    {t(`policies.${key}.title`)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6, fontSize: '0.875rem' }}>
                    {t(`policies.${key}.content`)}
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            keepMounted={false}
            disableRestoreFocus
            maxWidth="md"
            fullWidth
            sx={{
                zIndex: 1450,
                '& .MuiDialog-paper': {
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                    {t('title')}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 2, maxHeight: '70vh', overflow: 'auto' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                        {error}
                    </Alert>
                )}

                {enrollmentSuccess && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }}>
                        {enrollmentSuccess}
                    </Alert>
                )}

                {enrollmentError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                        {enrollmentError}
                    </Alert>
                )}

                <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                    <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                        {t('paymentOptions')}
                    </FormLabel>
                    <RadioGroup
                        value={paymentMethod}
                        onChange={(e) => {
                            setPaymentMethod(e.target.value);
                            setError('');
                        }}
                        sx={{ gap: 1 }}
                    >
                        <FormControlLabel
                            value="Self"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {t('payPersonally')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {t('payPersonallyDesc')}
                                    </Typography>
                                </Box>
                            }
                            sx={{
                                border: paymentMethod === 'Self' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                borderRadius: '8px',
                                p: 1.5,
                                m: 0,
                                '&:hover': {
                                    backgroundColor: '#f9fafb'
                                }
                            }}
                        />
                        <FormControlLabel
                            value="EIP"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {t('applyEip')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {t('applyEipDesc')}
                                    </Typography>
                                </Box>
                            }
                            sx={{
                                border: paymentMethod === 'EIP' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                borderRadius: '8px',
                                p: 1.5,
                                m: 0,
                                '&:hover': {
                                    backgroundColor: '#f9fafb'
                                }
                            }}
                        />
                    </RadioGroup>
                </FormControl>

                <Box sx={{ mb: 2 }}>
                    <Accordion
                        defaultExpanded={false}
                        TransitionProps={{ unmountOnExit: true }}
                        sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: '#f3f4f6' }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PolicyIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#111827' }}>
                                    {t('policiesTitle')}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                {POLICY_KEYS.map((key, index) => (
                                    <React.Fragment key={key}>
                                        {index > 0 && <Divider />}
                                        {renderPolicySection(key)}
                                    </React.Fragment>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={termsAccepted}
                                onChange={(e) => {
                                    setTermsAccepted(e.target.checked);
                                    setError('');
                                }}
                                sx={{
                                    color: '#3b82f6',
                                    '&.Mui-checked': {
                                        color: '#3b82f6'
                                    },
                                    py: 0
                                }}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem', lineHeight: 1.5 }}>
                                {t('acceptTerms')}
                            </Typography>
                        }
                        sx={{
                            alignItems: 'center',
                            m: 0
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                        color: '#6b7280'
                    }}
                >
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !paymentMethod || !termsAccepted}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 3,
                        backgroundColor: '#3b82f6',
                        '&:hover': {
                            backgroundColor: '#2563eb'
                        }
                    }}
                >
                    {loading ? t('enrolling') : t('enrollNow')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EnrollmentFormModal;
